import { useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { UserCircle } from 'lucide-react';
import { trackButtonClick } from '../../utils/analytics';

const DEFAULT_NAV_ORDER = [
  { id: 'features', label: 'Features' },
  { id: 'customization', label: 'Make It Yours' },
  { id: 'demo', label: 'Product Demo' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'faq', label: 'FAQ' },
  { id: 'contact', label: 'Contact' },
];

export function LandingNav({ content, theme, t, user, mobileMenuOpen, setMobileMenuOpen, mobileMenuRef, scrollToSection, navigate, goToSignup, logoUrl, appName }) {
  const saved = content?.branding?.nav_order || [];
  const savedIds = new Set(saved.map(s => s.id));
  const navOrder = saved.length > 0 ? [...saved, ...DEFAULT_NAV_ORDER.filter(d => !savedIds.has(d.id))] : DEFAULT_NAV_ORDER;
  const navLabels = content?.branding?.nav_labels || {};

  const navLinks = navOrder
    .filter(item => item.visible !== false)
    .map(item => ({
      id: item.id,
      label: navLabels[item.id] || item.label,
      target: item.target || item.id,
    }));

  const handleNavClick = (e, target) => {
    if (!target) return;
    // External URL
    if (/^https?:\/\//i.test(target)) {
      e.preventDefault();
      window.open(target, '_blank', 'noopener');
      setMobileMenuOpen(false);
      return;
    }
    // Internal route (starts with '/')
    if (target.startsWith('/')) {
      e.preventDefault();
      setMobileMenuOpen(false);
      navigate(target);
      return;
    }
    // Section scroll (default)
    scrollToSection(e, target);
  };

  const linkHref = (target) => {
    if (!target) return '#';
    if (/^https?:\/\//i.test(target) || target.startsWith('/')) return target;
    return `#${target}`;
  };

  const appNameMobileShow = content?.branding?.app_name_mobile_show !== false;
  const appNameMobileSize = content?.branding?.app_name_mobile_size || 'base';
  const autoHideOnScroll = content?.branding?.nav_auto_hide === true;
  const mobileSizeClass =
    appNameMobileSize === 'sm' ? 'text-sm' :
    appNameMobileSize === 'lg' ? 'text-2xl' :
    appNameMobileSize === 'xl' ? 'text-3xl' :
    'text-xl';

  const [hidden, setHidden] = useState(false);
  // Nav background mode while the visitor is over the hero. Three modes:
  //   'solid'       — always solid theme bg (no special behaviour).
  //   'frosted'     — translucent + backdrop blur over hero, solid past it.
  //   'transparent' — no bg / no border ONLY at the very top of the page;
  //                   reverts to solid theme bg the moment the visitor
  //                   starts scrolling. Lets the hero make a clean first
  //                   impression but clarifies the nav as a structural
  //                   element the second the page engages.
  // Legacy `navFrostedOverHero=true` maps to 'frosted' for backwards compat.
  //
  // The hero may be `position: sticky` (stickyContent mode), which fools an
  // IntersectionObserver into thinking it's always in view. We instead read
  // the hero's natural document position via offsetTop + offsetHeight and
  // compare against window.scrollY — that ignores any sticky translation.
  const navHeroStyle = content?.branding?.navHeroStyle
    || (content?.branding?.navFrostedOverHero === true ? 'frosted' : 'solid');
  const heroStyled = navHeroStyle === 'frosted' || navHeroStyle === 'transparent';
  const [overHero, setOverHero] = useState(heroStyled);
  useEffect(() => {
    if (!heroStyled) { setOverHero(false); return undefined; }
    const NAV_H = 64; // sticky nav height (h-16)
    // Transparent: any scroll at all (> ~4px tolerance) reverts to solid.
    // Frosted: stay frosted until the visitor has scrolled past the hero.
    const isAtTransparentZone = () => {
      if (navHeroStyle === 'transparent') return window.scrollY < 4;
      const hero = document.getElementById('hero') || document.querySelector('[data-section="hero"]');
      if (!hero) return false;
      return window.scrollY < (hero.offsetTop + hero.offsetHeight) - NAV_H;
    };
    setOverHero(isAtTransparentZone());
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        setOverHero(isAtTransparentZone());
        ticking = false;
      });
    };
    const onResize = () => setOverHero(isAtTransparentZone());
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [heroStyled, navHeroStyle]);
  useEffect(() => {
    if (!autoHideOnScroll) { setHidden(false); return; }
    // Auto-hide is mobile/tablet only — keep nav permanently visible on desktop (lg+)
    const desktopMq = window.matchMedia('(min-width: 1024px)');
    if (desktopMq.matches) { setHidden(false); return; }
    let lastY = window.scrollY;
    // Accumulate scroll delta in one direction so noisy 1-2px phantom scroll
    // events (iOS Safari address bar, layout reflows from autoplaying
    // carousels, bounce-scroll) can't flip the nav state. We only toggle
    // when sustained movement in one direction crosses the threshold.
    let accumDown = 0;
    let accumUp = 0;
    let lastFlipAt = 0;
    const FLIP_COOLDOWN_MS = 400;
    const DOWN_THRESHOLD = 60;  // px of accumulated down-scroll to hide
    const UP_THRESHOLD = 30;    // px of accumulated up-scroll to show
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - lastY;
        lastY = y;
        ticking = false;
        // Always show near the top or when mobile menu is open
        if (y < 80 || mobileMenuOpen) {
          accumDown = 0; accumUp = 0;
          setHidden(false);
          return;
        }
        // Ignore zero-delta events entirely (browser noise)
        if (dy === 0) return;
        if (dy > 0) {
          accumDown += dy;
          accumUp = 0;
        } else {
          accumUp += -dy;
          accumDown = 0;
        }
        const now = Date.now();
        if (now - lastFlipAt < FLIP_COOLDOWN_MS) return;
        if (accumDown >= DOWN_THRESHOLD) {
          accumDown = 0;
          lastFlipAt = now;
          setHidden(true);
        } else if (accumUp >= UP_THRESHOLD) {
          accumUp = 0;
          lastFlipAt = now;
          setHidden(false);
        }
      });
    };
    const onBreakpoint = (e) => {
      if (e.matches) setHidden(false);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    desktopMq.addEventListener?.('change', onBreakpoint);
    return () => {
      window.removeEventListener('scroll', onScroll);
      desktopMq.removeEventListener?.('change', onBreakpoint);
    };
  }, [autoHideOnScroll, mobileMenuOpen]);

  // Pick the right surface for the current scroll position + admin choice.
  // When the drawer is open AND the nav is in transparent-over-hero mode,
  // force the solid state at every screen size so the drawer has a proper
  // contrasting surface behind it (reuses the same slide-up + background
  // fade transition the scroll listener triggers).
  const forceSolidForOpenMenu = mobileMenuOpen;
  const isTransparent = heroStyled && overHero && navHeroStyle === 'transparent' && !forceSolidForOpenMenu;
  let navSurface;
  if (heroStyled && overHero && navHeroStyle === 'frosted') {
    navSurface = 'bg-white/10 backdrop-blur-md border-b border-white/15';
  } else if (isTransparent) {
    navSurface = 'bg-transparent';
  } else {
    navSurface = `${t.nav} backdrop-blur-sm ${theme === 'dark' ? t.border + ' border-b' : ''}`;
  }

  return (
    <nav className={`sticky top-0 z-50 ${isTransparent ? 'pt-4' : ''} ${navSurface} transition-[background-color,backdrop-filter,border-color,padding-top,transform] duration-300 ease-out ${hidden ? '-translate-y-full' : 'translate-y-0'}`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 gap-3">
          <div 
            className="flex items-center gap-3 cursor-pointer min-w-0 flex-1 landing-nav-fadein landing-nav-fadein--left"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            <img src={logoUrl} alt={appName} className="w-10 h-10 object-contain shrink-0" loading="eager" decoding="async" fetchPriority="high" />
            <span
              className={`${appNameMobileShow ? 'block' : 'hidden'} sm:block ${mobileSizeClass} sm:text-xl font-bold ${theme === 'light' ? 'text-white' : t.text} leading-tight break-words line-clamp-2`}
              data-testid="landing-nav-app-name"
            >
              {appName}
            </span>
          </div>
          
          {/* Header actions — burger menu shown on all devices */}
          <div className="flex items-center gap-2 landing-nav-fadein landing-nav-fadein--right">
            <Button 
              variant="ghost" 
              size="icon"
              className={`${t.navLink} hover:text-[#007a33]`}
              onClick={() => { trackButtonClick('nav_sign_in', 'Sign In'); navigate('/login'); }}
              data-testid="nav-sign-in-btn"
              aria-label="Sign in"
              title="Sign in"
            >
              <UserCircle className="w-5 h-5" />
            </Button>
            {!user && content?.branding?.nav_cta_visible !== false && (
            <Button 
              size="sm"
              className={theme === 'light' ? 'bg-white text-[#007a33] hover:bg-gray-100' : 'bg-[#007a33] hover:bg-[#005c26] text-white'}
              onClick={() => { trackButtonClick('nav_get_started', 'Get Started'); goToSignup(); }}
              data-testid="nav-cta-btn"
            >
              Get Started
            </Button>
            )}
            <button 
              className={`inline-flex items-center justify-center w-10 h-10 ${t.navLink}`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-burger-btn"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              <span className={`burger-squeeze ${mobileMenuOpen ? 'is-open' : ''}`}>
                <span></span><span></span><span></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Slide-down navigation (all devices) */}
      <div 
        ref={mobileMenuRef}
        className={`${t.mobileMenu} overflow-hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
        data-testid="nav-drawer"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 lg:py-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-center lg:gap-8 lg:gap-y-0">
          {navLinks.map((link, i) => {
            // Stagger delays follow the drawer's motion direction:
            //   open  → wait for the container to start expanding, then
            //           cascade items in (180ms base + i * 45ms)
            //   close → items lift out bottom-to-top ((last-index) * 30ms, faster)
            const total = navLinks.length;
            const delayMs = mobileMenuOpen ? 180 + i * 45 : (total - 1 - i) * 30;
            return (
              <a
                key={link.id}
                href={linkHref(link.target)}
                className={`${t.navLink} py-2 lg:py-0 whitespace-nowrap mobile-nav-item ${mobileMenuOpen ? 'is-open' : 'is-closing'}`}
                style={{ animationDelay: `${delayMs}ms` }}
                onClick={(e) => handleNavClick(e, link.target)}
              >
                {link.label}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
